import React, { useEffect, useState } from "react";
import "./App.css";
import { useNavigate, useLocation } from "react-router-dom";
import { db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";
import bymoLogoAdmin from "./img/bymoadmin.png"; // Adjust path to your logo



const AdminPage = () => {
    const [collections, setCollections] = useState({});
    const [editedCollections, setEditedCollections] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const [customIds, setCustomIds] = useState({}); // Store custom IDs for each collection
    // State for custom IDs and error messages for each collection
    const [errorMessages, setErrorMessages] = useState({});
    const [isAuthorized, setIsAuthorized] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const email = location.state?.email;
    const [newCollectionName, setNewCollectionName] = useState("");
    const [calculatorStates, setCalculatorStates] = useState({});


    // create new user >
    const handleCreateCollection = async () => {
        if (!newCollectionName.trim()) {
            setError("Please provide a valid collection name.");
            return;
        }

        try {
            setError(""); // Clear any previous errors
            // Call the API to create the collection (to be implemented)
            const response = await fetch(
                "https://bymolive-backend-995926394015.us-central1.run.app/api/create-collection",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ collection: newCollectionName }),
                }
            );

            if (!response.ok) {
                throw new Error("Failed to create collection.");
            }



            // Refresh collections after creation (optional based on API response)
            const data = await response.json();
            console.log("Collection created:", data);

            // Refresh the page to reflect the new collection
            window.location.reload();

            // Reset input field
            setNewCollectionName("");
        } catch (err) {
            console.error("Error creating collection:", err);
            setError("An error occurred while creating the collection. Please try again.");
        }
    };

    useEffect(() => {
        const checkAuthorization = async () => {
            if (!email) {
                navigate("/admin-login");
                return;
            }

            try {
                const adminDoc = await getDoc(doc(db, "admin", email));
                if (adminDoc.exists()) {
                    setIsAuthorized(true);
                } else {
                    navigate("/admin-login");
                }
            } catch (err) {
                console.error("Error checking admin authorization:", err);
                navigate("/admin-login");
            }
        };

        checkAuthorization();
    }, [email, navigate]);

    useEffect(() => {
        const fetchCollections = async () => {
            try {
                setLoading(true);

                // Fetch data from the backend API
                const response = await fetch(
                    "https://bymolive-backend-995926394015.us-central1.run.app/api/all-collections"
                );
                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.error || "Failed to fetch collections");
                }

                setCollections(data); // Set collections data
                setEditedCollections(data); // Initialize edited collections
            } catch (err) {
                console.error("Error fetching collections:", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (isAuthorized) {
            fetchCollections();
        }
    }, [isAuthorized]);

    // Render a loading state while checking authorization
    if (!isAuthorized && loading) {
        return <p>Loading...</p>;
    }

    if (!isAuthorized) {
        return null; // Prevent rendering if unauthorized
    }

    const calculateCommissions = (documents) => {
        const summary = {};
        let balance = 0;

        documents.forEach((doc) => {
            const status = doc.status?.toLowerCase() || "";
            const comision = parseFloat(doc.comision) || 0;

            // Skip rows with empty or unknown status
            if (!status || status === "unknown") return;

            if (!summary[status]) {
                summary[status] = 0;
            }
            summary[status] += comision;

            // Calculate balance for statuses "aprobat" and "user"
            if (["aprobat", "user"].includes(status)) {
                balance += comision;
            }
        });

        return { summary, balance };
    };

    const handleEdit = (collectionName, docId, field, value) => {
        setIsEditing(true);

        setEditedCollections((prev) => ({
            ...prev,
            [collectionName]: prev[collectionName].map((doc) =>
                doc.id === docId ? { ...doc, [field]: value } : doc
            ),
        }));
    };

    const handleCancel = () => {
        setEditedCollections(collections); // Revert to original data
        setIsEditing(false);
    };

    const Calculator = ({ calculatorValues, onCalculatorChange }) => {
        const handleCalculatorChange = (e) => {
            const sumaInclTva = e.target.value; // Get the raw value as a string
            const numericValue = parseFloat(sumaInclTva) || 0;
            const amountFtva = numericValue / 1.19;
            const commission = amountFtva * 0.4;

            // Update the calculator state via the parent callback
            onCalculatorChange({
                ...calculatorValues,
                sumaInclTva, // Keep this as a string for seamless input
                amountFtva: amountFtva.toFixed(2),
                commission: commission.toFixed(2),
            });
        };

        return (
            <div>
                <div className="calculator-field">
                    <label>Suma incl TVA:</label>
                    <input
                        type="number"
                        className="calculator-input"
                        value={calculatorValues.sumaInclTva} // Controlled input
                        onChange={handleCalculatorChange} // Update on every keystroke
                    />
                </div>
                <div className="calculator-field">
                    <label>Amount f TVA:</label>
                    <input
                        type="text"
                        className="calculator-input"
                        value={calculatorValues.amountFtva}
                        readOnly
                    />
                </div>
                <div className="calculator-field">
                    <label>Comision:</label>
                    <input
                        type="text"
                        className="calculator-input"
                        value={calculatorValues.commission}
                        readOnly
                    />
                </div>
            </div>
        );
    };








    const handleSave = async () => {
        try {
            setLoading(true);

            // Validate all edited documents before saving
            for (const [collectionName, documents] of Object.entries(editedCollections)) {
                for (const doc of documents) {
                    if (!doc.id || doc.id.trim() === "") {
                        setError("Please ensure all documents have a valid order ID.");
                        setLoading(false);
                        return;
                    }
                }
            }

            // Update the database with edited data
            for (const [collectionName, documents] of Object.entries(editedCollections)) {
                for (const doc of documents) {
                    const response = await fetch(
                        `https://bymolive-backend-995926394015.us-central1.run.app/api/update-collection`,
                        {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify({ collection: collectionName, document: doc }),
                        }
                    );

                    if (!response.ok) {
                        throw new Error("Failed to save changes");
                    }
                }
            }

            setCollections(editedCollections); // Update the original collections
            setIsEditing(false);
            setError(""); // Clear any previous error messages
        } catch (err) {
            console.error("Error saving changes:", err);
            setError("Failed to save changes. Please try again.");
        } finally {
            setLoading(false);
        }
    };


    const handleAddDocument = async (collectionName, customId) => {
        try {
            const response = await fetch(
                "https://bymolive-backend-995926394015.us-central1.run.app/api/add-document",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ collection: collectionName, id: customId }),
                }
            );

            if (!response.ok) {
                throw new Error("Failed to add document");
            }

            const data = await response.json();

            // Refresh the collections data after adding the new document
            const updatedCollections = await fetch(
                "https://bymolive-backend-995926394015.us-central1.run.app/api/all-collections"
            );
            const newData = await updatedCollections.json();
            setCollections(newData);
            setEditedCollections(newData);

            // Clear input field after adding
            setCustomIds((prev) => ({
                ...prev,
                [collectionName]: "",
            }));
        } catch (err) {
            console.error("Error adding document:", err);
            setErrorMessages((prev) => ({
                ...prev,
                [collectionName]: "Failed to add document. Please try again.",
            }));
        }
    };


    const handleAddDocumentWithValidation = (collectionName) => {
        const customId = customIds[collectionName] || "";

        if (!customId || customId.trim() === "") {
            // Set error message for the specific collection
            setErrorMessages((prev) => ({
                ...prev,
                [collectionName]: "Please enter a valid ID.",
            }));
            return;
        }

        // Clear error message for the specific collection
        setErrorMessages((prev) => ({
            ...prev,
            [collectionName]: "",
        }));

        // Call the function to add the document
        handleAddDocument(collectionName, customId);
    };


    const renderTable = (collectionName, documents) => {
        // Extract the `00info` document for additional details
        const infoDoc = documents.find((doc) => doc.id === "00info");
        const codReducere = infoDoc?.["Cod Reducere"] || "N/A";
        const comision = infoDoc?.Comision || "N/A";
        const calculatorState = calculatorStates[collectionName] || { sumaInclTva: "", amountFtva: "", commission: "" };


        // Filter out the `00info` document for table rows
        const filteredDocuments = documents.filter((doc) => doc.id !== "00info");

        if (filteredDocuments.length === 0) return null; // Skip rendering empty tables







        // Ensure 'id' is the first column
        const headers = [
            "id",
            ...Array.from(
                new Set(filteredDocuments.flatMap((doc) => Object.keys(doc).filter((key) => key !== "id")))
            ),
        ];

        const handleEditInfoDoc = (field, value) => {
            setEditedCollections((prev) => ({
                ...prev,
                [collectionName]: prev[collectionName].map((doc) =>
                    doc.id === "00info" ? { ...doc, [field]: value } : doc
                ),
            }));
        };

        const handleSaveInfoDoc = async () => {
            try {
                setLoading(true);
                const updatedInfoDoc = editedCollections[collectionName].find((doc) => doc.id === "00info");


                const response = await fetch(
                    "https://bymolive-backend-995926394015.us-central1.run.app/api/update-info",
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            collection: collectionName,
                            document: updatedInfoDoc,
                        }),
                    }
                );

                if (!response.ok) throw new Error("Failed to update 00info document.");

                // Reload the page to fetch the latest data
                window.location.reload();
            } catch (err) {
                console.error("Error updating 00info document:", err);
                setError("Failed to save changes to 00info. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        const handleDeleteCollection = async () => {
            try {
                setLoading(true);

                const response = await fetch(
                    "https://bymolive-backend-995926394015.us-central1.run.app/api/delete-collection",
                    {
                        method: "DELETE",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({ collection: collectionName }),
                    }
                );

                if (!response.ok) {
                    throw new Error("Failed to delete collection.");
                }

                const updatedCollections = { ...collections };
                delete updatedCollections[collectionName];

                setCollections(updatedCollections);
                setEditedCollections(updatedCollections);
            } catch (err) {
                console.error("Error deleting collection:", err);
                setError("Failed to delete collection. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        const handleDeleteDocument = async (docId) => {
            try {
                setLoading(true);

                const response = await fetch(
                    "https://bymolive-backend-995926394015.us-central1.run.app/api/delete-document",
                    {
                        method: "DELETE",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({ collection: collectionName, id: docId }),
                    }
                );

                if (!response.ok) {
                    throw new Error("Failed to delete document.");
                }

                const updatedCollections = { ...collections };
                updatedCollections[collectionName] = updatedCollections[collectionName].filter(
                    (doc) => doc.id !== docId
                );

                setCollections(updatedCollections);
                setEditedCollections(updatedCollections);
            } catch (err) {
                console.error("Error deleting document:", err);
                setError("Failed to delete document. Please try again.");
            } finally {
                setLoading(false);
            }
        };



        const { summary, balance } = calculateCommissions(filteredDocuments);

        return (
            <div key={collectionName} className="admin-collection-box">
                <div className="admin-collection-header">
                    <div className="admin-header-title-container">
                        <h3 className="admin-collection-title">
                            {collectionName}{" "}
                            <span className="info-details">
                        - {codReducere} - {comision}
                    </span>
                        </h3>
                        <button className="delete-collection-button" onClick={handleDeleteCollection}>
                            Delete user
                        </button>
                    </div>
                </div>
                <div className="commission-summary">
                    {Object.entries(summary).map(([status, total]) => (
                        <div key={status} className="commission-summary-item">
                            <strong>{status}:</strong> {total.toFixed(2)} RON
                        </div>
                    ))}
                    <div className="commission-summary-item">
                        <strong>Balance:</strong> {balance.toFixed(2)} RON
                    </div>
                </div>
                <table className="admin-table">
                    <thead>
                    <tr>
                        {headers.map((header) => (
                            <th key={header}>{header}</th>
                        ))}
                        <th>Delete</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filteredDocuments.map((doc) => (
                        <tr key={doc.id}>
                            {headers.map((header) => (
                                <td key={header}>
                                    {header === "id" ? (
                                        doc[header]
                                    ) : header === "status" ? (
                                        <select
                                            className="main-table-select-field"
                                            value={doc[header] || ""}
                                            onChange={(e) =>
                                                handleEdit(collectionName, doc.id, header, e.target.value)
                                            }
                                        >
                                            <option value="">Select Status</option>
                                            <option value="in asteptare">in asteptare</option>
                                            <option value="aprobat">aprobat</option>
                                            <option value="user">user</option>
                                            <option value="retur">retur</option>
                                        </select>
                                    ) : (
                                        <input
                                            type="text"
                                            className="main-table-input-field"
                                            value={doc[header] || ""}
                                            onChange={(e) =>
                                                handleEdit(collectionName, doc.id, header, e.target.value)
                                            }
                                        />
                                    )}
                                </td>
                            ))}
                            <td>
                                <button
                                    className="delete-document-button"
                                    onClick={() => handleDeleteDocument(doc.id)}
                                >
                                    DELETE
                                </button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>

                <div className="add-command-container">
                    <p className="add-command-label">Comanda noua:</p>
                    <div className="add-command-input-container">
                        <input
                            type="text"
                            className="add-command-input"
                            value={customIds[collectionName] || ""}
                            onChange={(e) =>
                                setCustomIds((prev) => ({ ...prev, [collectionName]: e.target.value }))
                            }
                        />
                        <button
                            className="add-command-button"
                            onClick={() => handleAddDocumentWithValidation(collectionName)}
                        >
                            Adauga
                        </button>
                    </div>
                    {errorMessages[collectionName] && (
                        <p className="error-message">{errorMessages[collectionName]}</p>
                    )}
                </div>

                {infoDoc && (
                    <div className="user-details-calculator">
                        <div className="user-details">
                            {Object.entries(infoDoc)
                                .filter(([key]) => key !== "id") // Exclude the "id" field
                                .map(([key, value]) => {
                                    const editedValue =
                                        editedCollections[collectionName]?.find((doc) => doc.id === "00info")?.[key];
                                    return (
                                        <div key={key} className="info-field">
                                            <label>{key}:</label>
                                            <input
                                                type="text"
                                                value={editedValue !== undefined ? editedValue : value}
                                                onChange={(e) => handleEditInfoDoc(key, e.target.value)}
                                                className="main-table-input-field"
                                            />
                                        </div>
                                    );
                                })}
                            <button className="save-info-button" onClick={handleSaveInfoDoc}>
                                Save User Data
                            </button>
                        </div>
                        <div className="calculator">
                            <Calculator
                                calculatorValues={calculatorState}
                                onCalculatorChange={(updatedValues) =>
                                    setCalculatorStates((prev) => ({
                                        ...prev,
                                        [collectionName]: updatedValues,
                                    }))
                                }
                            />
                        </div>
                    </div>

                )}

            </div>
        );

    };









    return (


        <div className="admin-container">
            {isEditing && (
                <div className="admin-buttons">
                    <p className="admin-buttons-text">
                        You have unsaved changes. Please save or cancel.
                    </p>
                    <div className="save-buttons">
                        <button onClick={handleSave} className="save-button">
                            Save
                        </button>
                        <button onClick={handleCancel} className="cancel-button">
                            Cancel
                        </button>
                    </div>
                </div>
            )}


            <div className="header-container">

                <div className="logo-container-admin">
                    <img src={bymoLogoAdmin} alt="BYMO Logo" className="bymo-logo-admin" />

                </div>
                <div className="add-collection-container">
                    <input
                        type="text"
                        placeholder="New collection name"
                        value={newCollectionName}
                        onChange={(e) => setNewCollectionName(e.target.value)}
                        className="new-collection-input"
                    />
                    <button
                        className="create-collection-button"
                        onClick={handleCreateCollection}
                    >
                        Create User
                    </button>
                </div>
            </div>

            {loading ? (
                <p className="admin-loading">Loading collections...</p>
            ) : error ? (
                <p className="admin-error-message">{error}</p>
            ) : (
                <div>
                    {Object.entries(editedCollections)
                        .filter(([collectionName]) => collectionName !== "admin")
                        .map(([collectionName, documents]) => (
                            <React.Fragment key={collectionName}>
                                {renderTable(collectionName, documents)}
                            </React.Fragment>
                        ))}


                </div>
            )}
        </div>
    );


};




export default AdminPage;
